import { Octokit } from '@octokit/rest';

export interface GitHubFile {
  name: string;
  path: string;
  type: 'file' | 'dir';
  size?: number;
  content?: string;
  download_url?: string;
}

export interface GitHubRepo {
  name: string;
  full_name: string;
  description: string;
  language: string;
  stars: number;
  forks: number;
}

export class GitHubService {
  private octokit: Octokit;
  private baseURL = 'https://api.github.com';

  constructor(token?: string) {
    // 对于公开仓库，可以不使用认证
    // 对于私有仓库，需要提供有效的token
    const auth = token && token !== 'your_real_token_here' ? token : undefined;
    this.octokit = new Octokit({
      auth,
    });
  }

  async getRepoInfo(owner: string, repo: string): Promise<GitHubRepo> {
    try {
      const response = await this.octokit.repos.get({
        owner,
        repo,
      });
      return {
        name: response.data.name,
        full_name: response.data.full_name,
        description: response.data.description || '',
        language: response.data.language || '',
        stars: response.data.stargazers_count,
        forks: response.data.forks_count,
      };
    } catch (error: any) {
      if (error.status === 404) {
        throw new Error(`Repository not found or private. Please check the repository URL and ensure you have access.`);
      } else if (error.status === 403) {
        throw new Error(`API rate limit exceeded or insufficient permissions. Please configure a GitHub Personal Access Token.`);
      }
      throw new Error(`Failed to fetch repository info: ${error.message || error}`);
    }
  }

  async getRepoContents(owner: string, repo: string, path = ''): Promise<GitHubFile[]> {
    try {
      const response = await this.octokit.repos.getContent({
        owner,
        repo,
        path,
      });

      if (Array.isArray(response.data)) {
        return response.data.map((item: any) => ({
          name: item.name,
          path: item.path,
          type: item.type === 'dir' ? 'dir' : 'file',
          size: item.size,
          download_url: item.download_url || undefined,
        }));
      } else {
        return [{
          name: response.data.name,
          path: response.data.path,
          type: 'file',
          size: response.data.size,
          download_url: response.data.download_url || undefined,
        }];
      }
    } catch (error) {
      throw new Error(`Failed to fetch repository contents: ${error}`);
    }
  }

  async getFileContent(owner: string, repo: string, path: string): Promise<string> {
    try {
      console.log(`=== getFileContent Debug ===`);
      console.log(`Fetching file content: ${owner}/${repo}/${path}`);
      console.log('Octokit auth configured:', !!this.octokit.auth);

      const response = await this.octokit.repos.getContent({
        owner,
        repo,
        path,
      });

      console.log('GitHub API response type:', Array.isArray(response.data) ? 'Array' : 'Single object');
      console.log('Response data keys:', Object.keys(response.data));

      if (!('content' in response.data)) {
        console.error('No content field in response. Response data:', response.data);
        throw new Error('No content field in GitHub API response');
      }

      if (typeof response.data.content !== 'string') {
        console.error('Content is not a string:', typeof response.data.content);
        throw new Error('Content is not a string');
      }

      console.log('Raw content length:', response.data.content.length);
      console.log('Content encoding:', response.data.encoding);

      // 在浏览器环境中使用atob解码base64
      try {
        let decoded: string;
        if (response.data.encoding === 'base64') {
          decoded = atob(response.data.content);
          console.log('Base64 decoded successfully, length:', decoded.length);
        } else {
          decoded = response.data.content;
          console.log('Using content directly (not base64), length:', decoded.length);
        }

        return decoded;
      } catch (error) {
        console.error('Decoding error:', error);
        throw new Error(`Failed to decode file content: ${error}`);
      }
    } catch (error: any) {
      console.error('=== Error in getFileContent ===');
      console.error('Error details:', error);
      console.error('Status:', error.status);
      console.error('Message:', error.message);

      if (error.status === 404) {
        throw new Error(`File not found: ${path}. The file may not exist or you may not have access.`);
      } else if (error.status === 403) {
        throw new Error(`Access denied (403). GitHub API rate limit or authentication issue.`);
      } else if (error.status === 401) {
        throw new Error(`Authentication failed (401). Invalid GitHub token.`);
      }

      throw new Error(`Failed to fetch file content (${error.status}): ${error.message || 'Unknown error'}`);
    }
  }

  parseGitHubUrl(url: string): { owner: string; repo: string } | null {
    const match = url.match(/github\.com\/([^/]+)\/([^/?#]+)/);
    if (match) {
      return {
        owner: match[1],
        repo: match[2].replace('.git', ''),
      };
    }
    return null;
  }

  getLanguageFromPath(path: string): string {
    const ext = path.split('.').pop()?.toLowerCase();
    const languageMap: Record<string, string> = {
      js: 'javascript',
      ts: 'typescript',
      jsx: 'javascript',
      tsx: 'typescript',
      py: 'python',
      java: 'java',
      cpp: 'cpp',
      c: 'c',
      h: 'c',
      cs: 'csharp',
      php: 'php',
      rb: 'ruby',
      go: 'go',
      rs: 'rust',
      swift: 'swift',
      kt: 'kotlin',
      scala: 'scala',
      html: 'html',
      css: 'css',
      scss: 'scss',
      sass: 'sass',
      less: 'less',
      json: 'json',
      xml: 'xml',
      yaml: 'yaml',
      yml: 'yaml',
      md: 'markdown',
      sql: 'sql',
      sh: 'shell',
      bash: 'shell',
      zsh: 'shell',
    };
    return languageMap[ext || ''] || 'plaintext';
  }
}